import { useMemo, useState } from "react";
import { SearchBar } from "@legal-500/legal500-design-system";
import type { EditionBpa } from "@/api/researchCms.service";
import { CmsFilterRow, StatusBadge } from "../../../../../components/ResearchCms";
import { ScheduledForMergeBadge } from "../BpaMerge/ScheduledForMergeBadge";
import { Named } from "./ColouredWords";
import { RingRadio } from "./controls/RingRadio";
import { ImportTable, type ImportTableColumn } from "./controls/ImportTable";
import { ImportSection } from "./ImportSection";
import shared from "./importShared.module.css";
import styles from "./SourceSection.module.css";

type SelectOption = { value: string; label: string };

type SourceSectionProps = {
  candidates: EditionBpa[];
  initialEntityId: string;
  loading?: boolean;
  selectedLabel: string;
  publicationOptions: SelectOption[];
  publicationId: string;
  editionOptions: SelectOption[];
  editionId: string;
  onPublicationChange: (publicationId: string) => void;
  onEditionChange: (editionId: string) => void;
  selectedId: string | null;
  onSelect: (editionBpaId: string) => void;
  fixed?: boolean;
  mergeBadges?: Record<string, { label: string; href: string }>;
};

function matchesSearch(bpa: EditionBpa, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [bpa.bpaName, bpa.rankingEntityName].some(text => text?.toLowerCase().includes(needle));
}

export function SourceSection({
  candidates,
  initialEntityId,
  loading = false,
  selectedLabel,
  publicationOptions,
  publicationId,
  editionOptions,
  editionId,
  onPublicationChange,
  onEditionChange,
  selectedId,
  onSelect,
  fixed = false,
  mergeBadges = {},
}: SourceSectionProps) {
  const [search, setSearch] = useState("");
  const [entityFilter, setEntityFilter] = useState(initialEntityId);
  const [changing, setChanging] = useState(false);

  const selected = candidates.find(bpa => bpa.id === selectedId) ?? null;
  const status = selected && !changing ? "collapsed" : "open";

  const entityOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const bpa of candidates) {
      if (bpa.rankingEntityId) byId.set(bpa.rankingEntityId, bpa.rankingEntityName);
    }
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [candidates]);

  const visible = candidates.filter(
    bpa => (!entityFilter || bpa.rankingEntityId === entityFilter) && matchesSearch(bpa, search),
  );

  function pick(editionBpaId: string) {
    onSelect(editionBpaId);
    setChanging(false);
  }

  const columns: ImportTableColumn<EditionBpa>[] = [
    {
      id: "pick",
      header: null,
      select: true,
      cell: bpa => (
        <label className={styles.pick}>
          <RingRadio
            name="import-source"
            aria-label={`Import from ${bpa.bpaName}`}
            checked={bpa.id === selectedId}
            onChange={() => pick(bpa.id)}
          />
        </label>
      ),
    },
    { id: "name", header: "Practice area", cell: bpa => bpa.bpaName },
    {
      id: "jurisdiction",
      header: "Jurisdiction",
      cell: bpa => <span className={shared.muted}>{bpa.rankingEntityName}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: bpa => (
        <span className={styles.statusCell}>
          <StatusBadge status={bpa.status} />
          {mergeBadges[bpa.id] && (
            <ScheduledForMergeBadge
              label={mergeBadges[bpa.id].label}
              href={mergeBadges[bpa.id].href}
            />
          )}
        </span>
      ),
    },
  ];

  return (
    <ImportSection
      number={1}
      title="Import from"
      description={status === "open" ? "Pick the practice area to bring content from." : undefined}
      status={status}
      valid={selected !== null}
      summary={
        selected && (
          <>
            <span>
              <Named bold side="incoming">
                {selectedLabel}
              </Named>{" "}
              <span className={shared.muted}>· {selected.rankingEntityName}</span>
            </span>
            {!fixed && (
              <button type="button" className={styles.change} onClick={() => setChanging(true)}>
                Change
              </button>
            )}
          </>
        )
      }
    >
      <div className={styles.picker}>
        <CmsFilterRow
          filters={[
            {
              key: "importPublication",
              label: "Publication",
              value: publicationId,
              onChange: onPublicationChange,
              options: publicationOptions,
              placeholder: "",
            },
            {
              key: "importEdition",
              label: "Edition",
              value: editionId,
              onChange: onEditionChange,
              options: editionOptions,
              placeholder: "",
            },
          ]}
        />
      </div>
      <div className={styles.filters}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search practice areas..." />
        <CmsFilterRow
          filters={[
            {
              key: "importJurisdiction",
              label: "Jurisdiction",
              value: entityFilter,
              onChange: setEntityFilter,
              options: [{ value: "", label: "All jurisdictions" }, ...entityOptions],
              placeholder: "All jurisdictions",
              searchable: true,
            },
          ]}
        />
      </div>
      {loading ? (
        <p className={styles.empty}>Loading practice areas...</p>
      ) : visible.length === 0 ? (
        <p className={styles.empty}>
          {candidates.length === 0
            ? "No other active practice areas in this edition."
            : "No practice areas match."}
        </p>
      ) : (
        <ImportTable
          ariaLabel="Practice areas to import from"
          columns={columns}
          rows={visible}
          onRowClick={bpa => pick(bpa.id)}
        />
      )}
    </ImportSection>
  );
}
